import { useAppSelector } from "@/redux/hooks";
import { Card, CardContent, CardFooter } from "@/shadcn/ui/card";
import { Badge } from "@/shadcn/ui/badge";
import { Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import {
    FileText,
    BookOpen,
    Eye,
    ShoppingCart,
    GraduationCap,
    Tag,
} from "lucide-react";

export default function ({ element }: any) {
    const { t } = useTranslation();
    const { currentLang }: any = usePage().props;
    const { dir } = useAppSelector((state) => state.locale);

    return (
        <Card
            dir={dir}
            className="group overflow-hidden border-gray-200 hover:border-teal-300 hover:shadow-2xl transition-all duration-300 bg-white h-full flex flex-col"
        >
            <Link
                href={`${route(`${currentLang}.frontend.document.show`, element.id)}?slug=${element.name}`}
                className="relative block overflow-hidden"
            >
                {/* Image Container */}
                <div className="relative h-40 sm:h-56 overflow-hidden bg-gray-100">
                    {/* Blurred backdrop */}
                    <img
                        src={element.thumb}
                        className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl"
                        alt=""
                        aria-hidden="true"
                    />
                    {/* Main image */}
                    <img
                        src={element.thumb}
                        className="relative w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                        alt={element.name}
                        loading="lazy"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Document Badge */}
                    <div className="absolute top-2 left-2 sm:top-3 sm:left-3">
                        {element.subject?.grade?.stage?.name && (
                            <Badge
                                variant="secondary"
                                className="bg-teal-600 text-white border-0 shadow-lg text-[10px] sm:text-xs px-2 py-0.5 sm:px-2.5 sm:py-0.5"
                            >
                                <FileText className="w-2.5 h-2.5 sm:w-3 sm:h-3 me-0.5 sm:me-1" />
                                {element.subject?.grade?.stage?.name}
                            </Badge>
                        )}
                    </div>

                    {/* Price Badge */}
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                        {element.price > 0 ? (
                            <Badge className="bg-white text-teal-700 border-0 shadow-lg font-bold text-[10px] sm:text-xs px-2 py-0.5 sm:px-2.5 sm:py-0.5">
                                {element.price} {t("kd")}
                            </Badge>
                        ) : (
                            <Badge className="bg-green-600 text-white border-0 shadow-lg text-[10px] sm:text-xs px-2 py-0.5 sm:px-2.5 sm:py-0.5">
                                {t("free")}
                            </Badge>
                        )}
                    </div>
                </div>
            </Link>

            <CardContent className="p-3 sm:p-5 flex-1 flex flex-col">
                <Link
                    href={`${route(`${currentLang}.frontend.document.show`, element.id)}?slug=${element.name}`}
                    className="flex-1 flex flex-col"
                >
                    {/* Document Title */}
                    <h3 className="text-sm sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3 line-clamp-2 group-hover:text-teal-600 transition-colors">
                        {element.name}
                    </h3>

                    {/* Subject Info */}
                    {element.subject && (
                        <div className="mb-2 sm:mb-3 space-y-1 sm:space-y-2">
                            {/* Subject Name with Icon */}
                            <div className="flex items-center gap-1 sm:gap-2">
                                <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 text-teal-600 flex-shrink-0" />
                                <span className="text-[10px] sm:text-sm font-semibold text-gray-900 truncate">
                                    {element.subject.name}
                                </span>
                            </div>

                            {/* Grade and Stage */}
                            <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs text-gray-600">
                                {element.subject.grade && (
                                    <>
                                        <GraduationCap className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                        <span>
                                            {element.subject.grade.name}
                                        </span>
                                    </>
                                )}
                                {element.subject.grade?.stage && (
                                    <>
                                        <span className="text-gray-400">·</span>
                                        <span>
                                            {element.subject.grade.stage.name}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    {element.description && (
                        <p className="text-[10px] sm:text-sm text-gray-600 line-clamp-2 sm:line-clamp-3 mb-2 sm:mb-3">
                            {element.description}
                        </p>
                    )}
                </Link>
            </CardContent>

            <CardFooter className="p-0 border-t border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 w-full sm:divide-x rtl:sm:divide-x-reverse">
                    <Link
                        href={`${route(`${currentLang}.frontend.document.show`, element.id)}?slug=${element.name}`}
                        className="flex items-center justify-center gap-1 sm:gap-2 py-2 sm:py-3 text-[10px] sm:text-sm font-medium text-white sm:text-gray-700 bg-teal-600 sm:bg-transparent hover:bg-teal-700 sm:hover:bg-teal-50 sm:hover:text-teal-700 transition-colors rounded-b-xl sm:rounded-b-none sm:rounded-bl-xl rtl:sm:rounded-bl-none rtl:sm:rounded-br-xl"
                    >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{t("view_details")}</span>
                    </Link>
                    <Link
                        href={`${route(`${currentLang}.frontend.document.show`, element.id)}?slug=${element.name}`}
                        className="hidden sm:flex items-center justify-center gap-2 py-3 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 transition-colors rounded-br-xl rtl:rounded-br-none rtl:rounded-bl-xl"
                    >
                        <ShoppingCart className="w-4 h-4" />
                        <span>{t("purchase")}</span>
                    </Link>
                </div>
            </CardFooter>
        </Card>
    );
}
